import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Lock, ArrowRight } from "lucide-react";

const BG_URL =
    "https://images.unsplash.com/photo-1527576539890-dfa815648363?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fGJsYWNrX2FuZF93aGl0ZXwxNzgzNDI5MjUyfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
    const { user, login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    if (user) return <Navigate to="/panel" replace />;

    async function onSubmit(e) {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await login(email, password);
            nav("/panel");
        } catch (e2) {
            setErr(formatApiError(e2?.response?.data?.detail) || "Giriş başarısız");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-5 bg-white">
            {/* Left: brand column */}
            <div
                className="hidden lg:block lg:col-span-3 relative bg-neutral-900"
                style={{
                    backgroundImage: `url(${BG_URL})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0 bg-black/55" />
                <div className="relative h-full flex flex-col justify-between p-14 text-white">
                    <div className="flex items-center gap-2">
                        <Lock size={16} strokeWidth={2.5} />
                        <span className="font-mono text-[11px] uppercase tracking-[0.2em]">
                            Yalnızca Yetkili Erişim
                        </span>
                    </div>
                    <div>
                        <div className="font-display text-6xl font-black leading-[0.95] tracking-tight max-w-lg">
                            Tahsilatın kontrol odası.
                        </div>
                        <div className="font-mono text-xs uppercase tracking-widest mt-6 text-white/70 max-w-md">
                            Cari bakiye · Fatura akışı · Tahsilat geçmişi · Görüşme notları — tek kanaldan, gerçek zamanlı.
                        </div>
                    </div>
                    <div className="flex items-center gap-8 border-t border-white/20 pt-6">
                        <div>
                            <div className="overline text-white/60">Sürüm</div>
                            <div className="font-mono text-sm">1.0 · GİZLİ</div>
                        </div>
                        <div>
                            <div className="overline text-white/60">Sistem</div>
                            <div className="font-mono text-sm">FERAHİS · TR</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right: form */}
            <div className="lg:col-span-2 flex items-center justify-center p-8 lg:p-14">
                <form
                    onSubmit={onSubmit}
                    className="w-full max-w-sm animate-fade-up"
                    data-testid="login-form"
                >
                    <div className="mb-10">
                        <div className="overline mb-3">01 · Kimlik Doğrulama</div>
                        <h1 className="font-display text-4xl font-black tracking-tight leading-none">
                            Giriş Yap
                        </h1>
                        <p className="text-sm text-neutral-500 mt-3">
                            Erişim kimliği ve şifreniz ile devam edin.
                        </p>
                    </div>

                    <label className="block mb-5">
                        <span className="overline block mb-2">E-posta</span>
                        <input
                            data-testid="login-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full border border-black/20 bg-white px-3.5 py-3 text-sm font-mono focus:outline-none focus:border-brand-blue"
                            placeholder="admin@ferahis.com"
                        />
                    </label>

                    <label className="block mb-6">
                        <span className="overline block mb-2">Şifre</span>
                        <input
                            data-testid="login-password"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full border border-black/20 bg-white px-3.5 py-3 text-sm font-mono focus:outline-none focus:border-brand-blue"
                            placeholder="••••••••"
                        />
                    </label>

                    {err && (
                        <div
                            data-testid="login-error"
                            className="border border-brand-red/30 bg-brand-red/5 text-brand-red text-xs font-mono px-3 py-2 mb-5"
                        >
                            {err}
                        </div>
                    )}

                    <button
                        data-testid="login-submit"
                        type="submit"
                        disabled={loading}
                        className="btn-sharp w-full justify-center disabled:opacity-60"
                    >
                        {loading ? "Doğrulanıyor…" : "Giriş"} <ArrowRight size={14} />
                    </button>

                    <div className="mt-10 border-t border-black/10 pt-4">
                        <div className="overline mb-1">Güvenlik notu</div>
                        <p className="text-xs text-neutral-500 leading-relaxed">
                            Bu sistem gizlidir. Yalnızca kendi kullanıcı bilgilerinizi kullanın. Kimlik bilgilerinizi
                            paylaşmayın.
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
}
